<template>
  <avs-card>
    <!-- ===== HEADER / TOOLBAR ===== -->
    <template #title>
      <v-toolbar flat class="mb-4 bgTable">
        <v-toolbar-title>Communiqués</v-toolbar-title>
        <v-spacer />
        <v-btn color="primary" @click="openCreate">
          <v-icon left>mdi-plus</v-icon>
          Nouveau
        </v-btn>
      </v-toolbar>
    </template>

    <!-- ===== CONTENT ===== -->
    <template #content>
    <!-- Filtres -->
    <v-row class="px-4">
      <v-col cols="12" md="6">
        <v-text-field
          v-model="search"
          label="Rechercher"
          prepend-inner-icon="mdi-magnify"
          dense
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-select
          v-model="stateFilter"
          :items="stateFilterOptions"
          label="État"
          dense
          hide-details
        />
      </v-col>
    </v-row>

    <!-- Table -->
    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :loading="loading"
      class="mt-3"
      :items-per-page="20"
      :footer-props="{ 'items-per-page-options': [10, 20, 50, -1] }"
    >
      <template #item.category="{ item }">
        <v-chip small :color="categoryColor(item.category)" text-color="white">
          <v-icon left small>{{ categoryIcon(item.category) }}</v-icon>
          {{ categoryLabel(item.category) }}
        </v-chip>
      </template>

      <template #item.start_date="{ item }">
        <span class="text-caption">{{ formatDateTime(item.start_date) }}</span>
      </template>

      <template #item.end_date="{ item }">
        <span class="text-caption">{{ formatDateTime(item.end_date) }}</span>
      </template>

      <template #item.is_active="{ item }">
        <v-switch
          v-model="item.is_active"
          dense
          hide-details
          :loading="item._togglingActive"
          @change="toggleActive(item)"
        />
      </template>

      <template #item.state="{ item }">
        <v-chip small :color="stateColor(item)" text-color="white">
          {{ stateLabel(item) }}
        </v-chip>
      </template>

      <template #item.actions="{ item }">
        <v-btn icon small @click="openEdit(item)">
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
        <v-btn icon small color="error" @click="openDeleteDialog(item)">
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Dialog Create/Edit -->
    <v-dialog v-model="dialog" max-width="640px" persistent>
      <v-card>
        <v-card-title>{{ editing ? "Modifier l'annonce" : "Nouvelle annonce" }}</v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="formValid">
            <v-text-field
              v-model="form.title"
              label="Titre"
              :rules="[v => !!v || 'Titre requis']"
              maxlength="200"
              counter
              required
            />
            <v-textarea
              v-model="form.message"
              label="Message"
              :rules="[v => !!v || 'Message requis']"
              rows="3"
              auto-grow
              required
            />
            <v-select
              v-model="form.category"
              :items="categoryOptions"
              label="Catégorie"
              required
            />
            <v-row>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.start_date"
                  label="Date de début"
                  type="datetime-local"
                  :rules="[v => !!v || 'Date de début requise']"
                  required
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  v-model="form.end_date"
                  label="Date de fin"
                  type="datetime-local"
                  :rules="endDateRules"
                  required
                />
              </v-col>
            </v-row>
            <v-text-field
              v-model="form.link_url"
              label="Lien (optionnel)"
              placeholder="https://..."
            />
            <v-text-field
              v-model="form.link_label"
              label="Libellé du lien (optionnel)"
              placeholder="En savoir plus"
            />
            <v-switch
              v-model="form.is_active"
              label="Annonce active"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">Annuler</v-btn>
          <v-btn color="primary" :loading="saving" :disabled="!formValid" @click="save">
            Enregistrer
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog suppression -->
    <v-dialog v-model="deleteDialog" max-width="420">
      <v-card v-if="deleteDialog && deleteTarget">
        <v-card-title class="error white--text">
          <v-icon left color="white">mdi-alert</v-icon>
          Supprimer cette annonce ?
        </v-card-title>
        <v-card-text class="pt-4">
          <strong>{{ deleteTarget.title }}</strong>
          <div class="mt-2 text-caption">
            Cette action est irréversible. L'annonce sera définitivement supprimée.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog = false">Annuler</v-btn>
          <v-btn color="error" :loading="deleting" @click="confirmDelete">Supprimer</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    </template>
  </avs-card>
</template>

<script>
import { AnnouncementService } from "@/services";

const CATEGORY_META = {
  info: { label: "Info", color: "blue", icon: "mdi-information" },
  promo: { label: "Promo", color: "green", icon: "mdi-tag-multiple" },
  warning: { label: "Alerte", color: "orange", icon: "mdi-alert" },
  urgent: { label: "Urgent", color: "red", icon: "mdi-alert-octagon" },
};

export default {
  name: "AdminAnnouncements",
  data: () => ({
    loading: false,
    items: [],
    search: "",
    stateFilter: "all",
    stateFilterOptions: [
      { value: "all", text: "Toutes" },
      { value: "live", text: "En cours" },
      { value: "scheduled", text: "Programmées" },
      { value: "expired", text: "Expirées" },
      { value: "paused", text: "Suspendues" },
    ],
    headers: [
      { text: "Titre", value: "title" },
      { text: "Catégorie", value: "category" },
      { text: "Début", value: "start_date" },
      { text: "Fin", value: "end_date" },
      { text: "Actif", value: "is_active", sortable: false },
      { text: "État", value: "state", sortable: false },
      { text: "Actions", value: "actions", sortable: false, align: "end" },
    ],
    categoryOptions: [
      { value: "info", text: "Info" },
      { value: "promo", text: "Promo" },
      { value: "warning", text: "Alerte" },
      { value: "urgent", text: "Urgent" },
    ],
    dialog: false,
    editing: null,
    formValid: false,
    saving: false,
    form: {
      title: "",
      message: "",
      category: "info",
      start_date: "",
      end_date: "",
      link_url: "",
      link_label: "",
      is_active: true,
    },
    // Dialog suppression
    deleteDialog: false,
    deleteTarget: null,
    deleting: false,
  }),

  computed: {
    filteredItems() {
      const q = (this.search || "").trim().toLowerCase();
      const now = new Date();
      return this.items.filter((item) => {
        if (q && !(item.title || "").toLowerCase().includes(q)
            && !(item.message || "").toLowerCase().includes(q)) {
          return false;
        }
        if (this.stateFilter !== "all") {
          const state = this.computeState(item, now);
          if (state !== this.stateFilter) return false;
        }
        return true;
      });
    },
    endDateRules() {
      return [
        v => !!v || "Date de fin requise",
        v => !v || !this.form.start_date || new Date(v) > new Date(this.form.start_date)
              || "La date de fin doit etre posterieure a la date de debut",
      ];
    },
  },

  created() {
    this.fetchItems();
  },

  methods: {
    async fetchItems() {
      this.loading = true;
      try {
        const data = await AnnouncementService.list();
        this.items = (data.results || data || []).map(it => ({ ...it, _togglingActive: false }));
      } catch (e) {
        console.error("Erreur chargement annonces:", e);
        this.send("Erreur de chargement", "error");
      } finally {
        this.loading = false;
      }
    },

    categoryColor(c) { return (CATEGORY_META[c] || CATEGORY_META.info).color; },
    categoryIcon(c) { return (CATEGORY_META[c] || CATEGORY_META.info).icon; },
    categoryLabel(c) { return (CATEGORY_META[c] || CATEGORY_META.info).label; },

    formatDateTime(iso) {
      if (!iso) return "-";
      return new Date(iso).toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
      });
    },

    computeState(item, now = new Date()) {
      if (!item.is_active) return "paused";
      const start = new Date(item.start_date);
      const end = new Date(item.end_date);
      if (now < start) return "scheduled";
      if (now > end) return "expired";
      return "live";
    },
    stateColor(item) {
      return {
        live: "green", scheduled: "blue", expired: "grey", paused: "orange",
      }[this.computeState(item)] || "grey";
    },
    stateLabel(item) {
      return {
        live: "En cours", scheduled: "Programmée", expired: "Expirée", paused: "Suspendue",
      }[this.computeState(item)] || "?";
    },

    openCreate() {
      this.editing = null;
      this.form = {
        title: "", message: "", category: "info",
        start_date: this.toLocalInput(new Date()),
        end_date: this.toLocalInput(new Date(Date.now() + 24 * 3600 * 1000)),
        link_url: "", link_label: "", is_active: true,
      };
      this.dialog = true;
    },

    openEdit(item) {
      this.editing = item;
      this.form = {
        title: item.title,
        message: item.message,
        category: item.category,
        start_date: this.toLocalInput(item.start_date),
        end_date: this.toLocalInput(item.end_date),
        link_url: item.link_url || "",
        link_label: item.link_label || "",
        is_active: item.is_active,
      };
      this.dialog = true;
    },

    toLocalInput(d) {
      // Convertit une date (ISO ou Date) en "YYYY-MM-DDTHH:MM" pour <input type=datetime-local>
      const dt = d instanceof Date ? d : new Date(d);
      const pad = (n) => String(n).padStart(2, "0");
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
    },

    closeDialog() {
      this.dialog = false;
      this.editing = null;
    },

    async save() {
      this.saving = true;
      const payload = {
        ...this.form,
        start_date: new Date(this.form.start_date).toISOString(),
        end_date: new Date(this.form.end_date).toISOString(),
      };
      try {
        if (this.editing) {
          await AnnouncementService.update(this.editing.id, payload);
          this.send("Annonce modifiée", "success");
        } else {
          await AnnouncementService.create(payload);
          this.send("Annonce créée", "success");
        }
        this.dialog = false;
        await this.fetchItems();
      } catch (e) {
        console.error("Erreur enregistrement:", e);
        const detail = e?.response?.data?.end_date?.[0] || e?.response?.data?.detail || "Erreur lors de l'enregistrement";
        this.send(detail, "error");
      } finally {
        this.saving = false;
      }
    },

    async toggleActive(item) {
      item._togglingActive = true;
      try {
        await AnnouncementService.update(item.id, { is_active: item.is_active });
        this.send(item.is_active ? "Annonce activée" : "Annonce suspendue", "success");
      } catch (e) {
        console.error("Erreur toggle:", e);
        item.is_active = !item.is_active; // rollback UI
        this.send("Erreur lors de la mise a jour", "error");
      } finally {
        item._togglingActive = false;
      }
    },

    openDeleteDialog(item) {
      this.deleteTarget = item;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.deleteTarget) return;
      const target = this.deleteTarget;
      this.deleting = true;
      try {
        await AnnouncementService.remove(target.id);
        this.send("Annonce supprimee", "success");
        this.deleteDialog = false;
        this.deleteTarget = null;
        await this.fetchItems();
      } catch (e) {
        console.error("Erreur suppression:", e);
        this.send("Erreur lors de la suppression", "error");
      } finally {
        this.deleting = false;
      }
    },

    send(msg, color) {
      this.$store.commit("app/message", { msg, color });
    },
  },
};
</script>
